import React, {memo, useCallback, useMemo, useState} from 'react';
import {PasswordContainer} from '../../../../components/PasswordContainer';
import {SettingsPageContainer} from '../../../../components/Settings/SettingsPageContainer';
import {useHistory, useParams} from 'react-router-dom';
import {useMessage} from '../../../../hooks/message';
import {useRPC} from '../../../../hooks/rpc';
import {CameraBrandModel} from '../../../../types/control';
import {useKeyboard} from '../../../../hooks/keyboard';
import {useSelector} from 'react-redux';
import {SettingInputComponent} from '../../../../components/Settings/SettingInputComponent';
import {SettingCheckBoxComponent} from '../../../../components/Settings/SettingCheckboxComponent';
import {isValidString} from '../../../../utils/validation';

const BACK_URL = '/developer/camera-brand';

export const CameraBrandEditScreen = memo(function CameraBrandEditScreen() {
    const params = useParams<{ id: string }>();
    // const question = useQuestion();
    const {success, failure} = useMessage();
    const [rpc] = useRPC();
    const history = useHistory();
    const [setActiveInput, renderKeyboard, currentInput] = useKeyboard();
    const [setActiveNumpadInput, renderNumpadKeyboard, currentNumpadInput] = useKeyboard('numpad', true);

    const cameraBrandList = useSelector(state => state.control.cameraBrandList);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const selected = useMemo(() => cameraBrandList.find(cameraBrand => cameraBrand.id === params?.id) || null, []);

    const [name, setName] = useState(selected?.name || '');
    const [isUSB, setIsUSB] = useState(selected?.is_usb || false);
    const [authenticationRequired, setAuthenticationRequired] = useState(selected?.authentication_required || false);

    const setAuthenticationRequiredCheckbox = useCallback(() => {
        setAuthenticationRequired(required => !required);
    }, []);

    const [hasRTSP, setHasRTSP] = useState(selected?.rtsp != null);
    const [rtsp720p, setRTSP720p] = useState((selected?.rtsp?.stream?.q_720p) || '');
    const [rtsp360p, setRTSP360p] = useState((selected?.rtsp?.stream?.q_360p) || '');

    const setHasRTSPCheckbox = useCallback(() => {
        setHasRTSP(hasRTSP => !hasRTSP);
    }, []);

    const [hasVideoCGI, setHasVideoCGI] = useState(selected?.video_cgi != null);
    const [videoCGI720p, setVideoCGI720p] = useState((selected?.video_cgi?.stream?.q_720p) || '');
    const [videoCGI360p, setVideoCGI360p] = useState((selected?.video_cgi?.stream?.q_360p) || '');

    const setHasVideoCGICheckbox = useCallback(() => {
        setHasVideoCGI(hasVideoCGI => !hasVideoCGI);
    }, []);

    const [hasMJPEG, setHasMJPEG] = useState(selected?.mjpeg != null);
    const [mjpeg720p, setMJPEG720p] = useState((selected?.mjpeg?.stream?.q_720p) || '');
    const [mjpeg360p, setMJPEG360p] = useState((selected?.mjpeg?.stream?.q_360p) || '');
    const [mjpegDelayMS, setMJPEGDelayMS] = useState((selected?.mjpeg?.delay_ms?.toString()) || '250');

    const setHasMJPEGCheckbox = useCallback(() => {
        setHasMJPEG(hasMJPEG => !hasMJPEG);
    }, []);

    const onBackPressed = useCallback(() => {
        history.push(BACK_URL);
    }, [history]);

    const setIsUSBCheckbox = useCallback(() => {
        setIsUSB(isUSB => !isUSB);
    }, []);

    const isFieldsValid = useCallback((): boolean => {
        if (name == null || name.length === 0) {
            failure('Lütfen isim giriniz.');

            return false;
        }

        if (isUSB) {
            return true;
        }

        if (!hasRTSP && !hasVideoCGI && !hasMJPEG) {
            failure('Lütfen desteklenen en az bir tip seçin.');

            return false;
        }

        if (hasRTSP) {
            if (!isValidString(rtsp720p) && !isValidString(rtsp360p)) {
                failure('Lütfen en az bir rtsp stream girin.');

                return false;
            }
        }

        if (hasVideoCGI) {
            if (!isValidString(videoCGI720p) && !isValidString(videoCGI360p)) {
                failure('Lütfen en az bir video cgi stream girin.');

                return false;
            }
        }

        if (hasMJPEG) {
            if (!isValidString(mjpeg720p) && !isValidString(mjpeg360p)) {
                failure('Lütfen en az bir mjpeg stream girin.');

                return false;
            }

            const delayMS = parseInt(mjpegDelayMS);
            if (delayMS < 100) {
                failure('Delay ms 100ms den küçük olamaz.');

                return false;
            }
        }

        return true;
    }, [failure, hasMJPEG, hasRTSP, hasVideoCGI, isUSB, mjpeg360p, mjpeg720p, mjpegDelayMS, name, rtsp360p, rtsp720p, videoCGI360p, videoCGI720p]);

    const onCreatePressed = useCallback(() => {
        if (!isFieldsValid()) {
            return;
        }

        const cameraBrandModel: CameraBrandModel = {
            id: selected?.id || null,
            name,
            is_usb: isUSB,
            authentication_required:authenticationRequired || false,
        };

        if (!isUSB) {
            if (hasRTSP) {
                cameraBrandModel.rtsp = {
                    stream: {
                        q_720p: rtsp720p || '',
                        q_360p: rtsp360p || '',
                    },
                };
            }

            if (hasVideoCGI) {
                cameraBrandModel.video_cgi = {
                    stream: {
                        q_720p: videoCGI720p || '',
                        q_360p: videoCGI360p || '',
                    },
                };
            }

            if (hasMJPEG) {
                cameraBrandModel.mjpeg = {
                    stream: {
                        q_720p: mjpeg720p || '',
                        q_360p: mjpeg360p || '',
                    },
                    delay_ms: parseInt(mjpegDelayMS) || 250,
                };
            }
        }

        return rpc.updateCameraBrand(cameraBrandModel)
            .then(() => {
                success('Kamera markası başarılı bir şekilde ' + (selected?.id == null ? 'kaydedildi.' : 'düzenlendi.'));
                onBackPressed();
            }).catch(failure);
    }, [
        isFieldsValid,
        selected?.id,
        name,
        isUSB,
        rpc,
        failure,
        hasRTSP,
        hasVideoCGI,
        hasMJPEG,
        authenticationRequired,
        rtsp720p,
        rtsp360p,
        videoCGI720p,
        videoCGI360p,
        mjpeg720p,
        mjpeg360p,
        mjpegDelayMS,
        success,
        onBackPressed,
    ]);

    const buttons = useMemo(() => [
        // {
        //     text: 'SİL',
        //     className: 'mulberry-wood',
        //     onClick: onDeletePressed,
        //     hidden: !(selected?.id != null),
        // },
        {
            text: 'KAYDET',
            className: 'green',
            onClick: onCreatePressed,
        },
        {
            text: 'İPTAL',
            className: 'red',
            url: BACK_URL,
        },
    ], [onCreatePressed/*, onDeletePressed, selected*/]);

    const onBlur = useCallback(() => {
        setActiveInput(null);
    }, [setActiveInput]);

    return (
        <PasswordContainer requiredRole='developer' isFullscreen={true}>
            <SettingsPageContainer
                className='service-edit'
                buttons={buttons}
                backUrl={BACK_URL}
                currentInput={currentInput}
                currentNumpadInput={currentNumpadInput}>
                <div className='content-divider'>
                    <div className='content-wrapper'>
                        <SettingInputComponent
                            placeholder='Kontrol adı yazınız...'
                            label='Kontrol Adı'
                            value={name}
                            setValue={setName}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}/>
                        <SettingCheckBoxComponent
                            label='USB Kamera'
                            set={setIsUSBCheckbox}
                            value={isUSB}/>
                        <SettingCheckBoxComponent
                            label='Giriş zorunlu'
                            set={setAuthenticationRequiredCheckbox}
                            value={authenticationRequired}/>
                        {
                            renderIf(!isUSB)(() => (
                                <>
                                    <div className='line'>
                                        <span>RTSP</span>
                                    </div>
                                    <SettingCheckBoxComponent
                                        label='Destekliyor'
                                        set={setHasRTSPCheckbox}
                                        value={hasRTSP}/>
                                    {
                                        renderIf(hasRTSP)(() => (
                                            <>
                                                <SettingInputComponent
                                                    placeholder='HD Stream URL'
                                                    label='HD Stream URL'
                                                    maxLength={75}
                                                    value={rtsp720p}
                                                    setValue={setRTSP720p}
                                                    setActiveInput={setActiveInput}
                                                    onBlur={onBlur}/>
                                                <SettingInputComponent
                                                    placeholder='SD Stream URL'
                                                    label='SD Stream URL'
                                                    maxLength={75}
                                                    value={rtsp360p}
                                                    setValue={setRTSP360p}
                                                    setActiveInput={setActiveInput}
                                                    onBlur={onBlur}/>
                                            </>
                                        ))
                                    }
                                </>
                            ))
                        }
                    </div>
                    <div className='content-wrapper'>
                        {
                            renderIf(!isUSB)(() => (
                                <>
                                    <div className='line'>
                                        <span>VideoCGI</span>
                                    </div>
                                    <SettingCheckBoxComponent
                                        label='Destekliyor'
                                        set={setHasVideoCGICheckbox}
                                        value={hasVideoCGI}/>
                                    {
                                        renderIf(hasVideoCGI)(() => (
                                            <>
                                                <SettingInputComponent
                                                    placeholder='HD Stream URL'
                                                    label='HD Stream URL'
                                                    maxLength={75}
                                                    value={videoCGI720p}
                                                    setValue={setVideoCGI720p}
                                                    setActiveInput={setActiveInput}
                                                    onBlur={onBlur}/>
                                                <SettingInputComponent
                                                    placeholder='SD Stream URL'
                                                    label='SD Stream URL'
                                                    maxLength={75}
                                                    value={videoCGI360p}
                                                    setValue={setVideoCGI360p}
                                                    setActiveInput={setActiveInput}
                                                    onBlur={onBlur}/>
                                            </>
                                        ))
                                    }
                                    <div className='line'>
                                        <span>MJPEG</span>
                                    </div>
                                    <SettingCheckBoxComponent
                                        label='Destekliyor'
                                        set={setHasMJPEGCheckbox}
                                        value={hasMJPEG}/>
                                    {
                                        renderIf(hasMJPEG)(() => (
                                            <>
                                                <SettingInputComponent
                                                    placeholder='Delay MS'
                                                    label='Delay MS'
                                                    value={mjpegDelayMS}
                                                    setValue={setMJPEGDelayMS}
                                                    setActiveInput={setActiveNumpadInput}
                                                    tempValue='0'
                                                    onBlur={onBlur}/>
                                                <SettingInputComponent
                                                    placeholder='HD Stream URL'
                                                    label='HD Stream URL'
                                                    maxLength={75}
                                                    value={mjpeg720p}
                                                    setValue={setMJPEG720p}
                                                    setActiveInput={setActiveInput}
                                                    onBlur={onBlur}/>
                                                <SettingInputComponent
                                                    placeholder='SD Stream URL'
                                                    label='SD Stream URL'
                                                    maxLength={75}
                                                    value={mjpeg360p}
                                                    setValue={setMJPEG360p}
                                                    setActiveInput={setActiveInput}
                                                    onBlur={onBlur}/>
                                            </>
                                        ))
                                    }
                                </>
                            ))
                        }
                    </div>
                </div>
            </SettingsPageContainer>
            {
                renderKeyboard()
            }
            {
                renderNumpadKeyboard()
            }
        </PasswordContainer>
    );
});
